/**
 * Cloud implementation of the shared CameraTagsApi — node-scoped.
 *
 * Tags live on the site, so every call goes to the SELECTED site through the tunnel
 * (/api/nodes/:nodeId/camera-tags → camera_tags_proxy → the node's /api/internal/camera-tags).
 * `scopeKey` is that site's id, so the shared catalogue cache never shows one site's tags on
 * another. With no site selected ("All Sites") every call fails with a stated reason.
 */
import type { CameraTagsApi } from '@trinetra/shared';
import {
  getNodeCameras, getNodeCameraTags, createNodeTagCategory, updateNodeTagCategory, deleteNodeTagCategory,
  createNodeTag, updateNodeTag, deleteNodeTag, bulkAssignNodeTags, getNodePtzDetectStatus, startNodePtzDetect,
} from '../services/api.service';
import { selectedNodeId } from './node';

function nodeId(): string {
  const id = selectedNodeId();
  if (!id) throw new Error('Pick a site first — camera tags belong to one site.');
  return id;
}

const EMPTY = { categories: [], ptz: { yes: 0, no: 0, unknown: 0 }, colors: [] };

export const cloudCameraTagsApi: CameraTagsApi = {
  scopeKey: () => `node:${selectedNodeId() || 'none'}`,
  async catalog() {
    if (!selectedNodeId()) return EMPTY;
    return (await getNodeCameraTags(nodeId())) || EMPTY;
  },
  createCategory: async (data) => createNodeTagCategory(nodeId(), data),
  updateCategory: async (id, data) => updateNodeTagCategory(nodeId(), id, data),
  deleteCategory: async (id) => deleteNodeTagCategory(nodeId(), id),
  createTag: async (categoryId, data) => createNodeTag(nodeId(), categoryId, data),
  updateTag: async (id, data) => updateNodeTag(nodeId(), id, data),
  deleteTag: async (id) => deleteNodeTag(nodeId(), id),
  bulkAssign: async (cameraIds, add, remove) => bulkAssignNodeTags(nodeId(), cameraIds, add, remove),
  detectStatus: async () => (await getNodePtzDetectStatus(nodeId())) || { running: false },
  startDetect: async (scope) => startNodePtzDetect(nodeId(), scope),
  async listAllCameras() {
    // The proxy caps a page at 1000 — enough for one site's list on every deployment so far.
    const data = await getNodeCameras(nodeId());
    return Array.isArray(data) ? data : data?.data || data?.cameras || [];
  },
};
